import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import { useNotifications } from '../../contexts/NotificationContext';

const RISK_LEVEL_CONFIG = {
  low: { label: 'Faible', color: '#28a745', bgColor: '#d4edda' },
  medium: { label: 'Moyen', color: '#ffc107', bgColor: '#fff3cd' },
  high: { label: 'Élevé', color: '#fd7e14', bgColor: '#ffe5d0' },
  critical: { label: 'Critique', color: '#dc3545', bgColor: '#f8d7da' }
};

const SecurityReportsTab = ({ token }) => {
  const { showSuccess, showError, showWarning } = useNotifications();
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [runningAnalysis, setRunningAnalysis] = useState(false);

  // Charger les rapports
  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/security-reports?limit=30`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setReports(data.data.reports);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Charger le statut du service
  const loadServiceStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/security-reports/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setServiceStatus(data.data);
      }
    } catch (err) {
      console.error('Erreur chargement statut:', err);
    }
  }, [token]);

  // Charger un rapport détaillé
  const loadReportDetail = async (reportDate) => {
    try {
      // Extraire la date au format YYYY-MM-DD (sans le timestamp)
      const dateOnly = reportDate.split('T')[0];
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/security-reports/${dateOnly}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedReport(data.data);
      }
    } catch (err) {
      console.error('Erreur chargement rapport:', err);
    }
  };

  // Lancer une analyse manuelle
  const runAnalysis = async () => {
    if (runningAnalysis) return;

    setRunningAnalysis(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/security-reports/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ hours: 24 })
      });
      const data = await response.json();

      if (data.success) {
        const riskLevel = data.data.analysis.risk_level;
        const message = `Analyse terminée - Niveau: ${RISK_LEVEL_CONFIG[riskLevel]?.label || riskLevel}`;

        if (riskLevel === 'critical' || riskLevel === 'high') {
          showWarning(message);
        } else {
          showSuccess(message);
        }
        await loadReports();
        // Sélectionner automatiquement le nouveau rapport
        if (data.data.report) {
          setSelectedReport(data.data.report);
        }
      } else {
        showError('Erreur: ' + data.error);
      }
    } catch (err) {
      showError('Erreur de connexion au serveur');
    } finally {
      setRunningAnalysis(false);
    }
  };

  useEffect(() => {
    loadReports();
    loadServiceStatus();
  }, [loadReports, loadServiceStatus]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Chargement...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: 0 }}>Rapports de Sécurité</h3>
          <p style={{ margin: '5px 0 0', color: '#666', fontSize: '14px' }}>
            Analyse quotidienne des logs par règles de détection
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {serviceStatus && (
            <span style={{
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              background: '#d4edda',
              color: '#155724'
            }}>
              {serviceStatus.analysisType === 'rule-based' ? 'Analyse par règles' : 'Actif'}
            </span>
          )}
          <button
            onClick={runAnalysis}
            disabled={runningAnalysis}
            style={{
              padding: '10px 20px',
              background: runningAnalysis ? '#6c757d' : 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: runningAnalysis ? 'wait' : 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {runningAnalysis ? 'Analyse en cours...' : 'Lancer une analyse'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Service Status - plus de configuration requise avec l'analyse par règles */}

      <div style={{ display: 'grid', gridTemplateColumns: selectedReport ? '1fr 2fr' : '1fr', gap: '20px' }}>
        {/* Liste des rapports */}
        <div>
          <h4 style={{ margin: '0 0 15px' }}>Historique ({reports.length})</h4>

          {reports.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#666', background: '#f8f9fa', borderRadius: '8px' }}>
              Aucun rapport disponible.<br />
              Lancez une analyse pour générer le premier rapport.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {reports.map(report => {
                const riskConfig = RISK_LEVEL_CONFIG[report.risk_level] || RISK_LEVEL_CONFIG.low;
                const isSelected = selectedReport?.id === report.id;

                return (
                  <div
                    key={report.id}
                    onClick={() => loadReportDetail(report.report_date)}
                    style={{
                      padding: '15px',
                      background: isSelected ? '#e3f2fd' : 'white',
                      border: isSelected ? '2px solid #2196f3' : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '600' }}>{formatDate(report.report_date)}</span>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: riskConfig.bgColor,
                        color: riskConfig.color
                      }}>
                        {riskConfig.label}
                      </span>
                    </div>
                    <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#666', lineHeight: '1.4' }}>
                      {report.summary?.substring(0, 100)}...
                    </p>
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#999', display: 'flex', gap: '15px' }}>
                      <span>{report.patterns_count} patterns</span>
                      <span>{report.suspicious_ips_count} IPs</span>
                      <span>{report.logs_analyzed_count} logs</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Détail du rapport sélectionné */}
        {selectedReport && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e0e0e0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
              <div>
                <h4 style={{ margin: 0 }}>Rapport du {formatDate(selectedReport.report_date)}</h4>
                <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#999' }}>
                  Analysé en {selectedReport.analysis_duration_ms}ms • {selectedReport.model_used === 'rule-based-v1' ? 'Analyse par règles' : selectedReport.model_used}
                </p>
              </div>
              <span style={{
                padding: '6px 14px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '600',
                background: RISK_LEVEL_CONFIG[selectedReport.risk_level]?.bgColor,
                color: RISK_LEVEL_CONFIG[selectedReport.risk_level]?.color
              }}>
                Risque {RISK_LEVEL_CONFIG[selectedReport.risk_level]?.label}
              </span>
            </div>

            {/* Résumé */}
            <div style={{ marginBottom: '20px' }}>
              <h5 style={{ margin: '0 0 10px', color: '#333' }}>Résumé</h5>
              <p style={{ margin: 0, lineHeight: '1.6', color: '#555' }}>{selectedReport.summary}</p>
            </div>

            {/* Patterns détectés */}
            {selectedReport.detected_patterns?.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h5 style={{ margin: '0 0 10px', color: '#333' }}>Patterns détectés</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {selectedReport.detected_patterns.map((pattern, idx) => (
                    <div key={idx} style={{
                      padding: '12px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      borderLeft: `4px solid ${RISK_LEVEL_CONFIG[pattern.severity]?.color || '#6c757d'}`
                    }}>
                      <div style={{ fontWeight: '600', marginBottom: '5px' }}>
                        {pattern.type} <span style={{ fontWeight: 'normal', color: '#666' }}>({pattern.severity}) - {pattern.count || 1} occurrence(s)</span>
                      </div>
                      <p style={{ margin: '0 0 5px', fontSize: '14px' }}>{pattern.description}</p>

                      {/* IPs sources */}
                      {pattern.source_ips?.length > 0 && (
                        <div style={{ margin: '8px 0', padding: '8px', background: '#fff', borderRadius: '4px', fontSize: '12px' }}>
                          <strong>IPs sources :</strong>
                          <div style={{ fontFamily: 'monospace', marginTop: '4px' }}>
                            {pattern.source_ips.join(', ') || 'N/A'}
                          </div>
                        </div>
                      )}

                      {/* User-Agents suspects */}
                      {pattern.sample_user_agents?.length > 0 && (
                        <div style={{ margin: '8px 0', padding: '8px', background: '#fff', borderRadius: '4px', fontSize: '12px' }}>
                          <strong>User-Agents :</strong>
                          <div style={{ fontFamily: 'monospace', marginTop: '4px', wordBreak: 'break-all' }}>
                            {pattern.sample_user_agents.map((ua, i) => (
                              <div key={i} style={{ marginBottom: '2px' }}>{ua}</div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* URLs ciblées */}
                      {pattern.sample_urls?.length > 0 && (
                        <div style={{ margin: '8px 0', padding: '8px', background: '#fff', borderRadius: '4px', fontSize: '12px' }}>
                          <strong>URLs ciblées :</strong>
                          <div style={{ fontFamily: 'monospace', marginTop: '4px', wordBreak: 'break-all' }}>
                            {pattern.sample_urls.map((url, i) => (
                              <div key={i} style={{ marginBottom: '2px' }}>{url}</div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Période d'activité */}
                      {(pattern.first_seen || pattern.last_seen) && (
                        <div style={{ margin: '8px 0', fontSize: '12px', color: '#666' }}>
                          <strong>Période :</strong> {pattern.first_seen ? new Date(pattern.first_seen).toLocaleString('fr-FR') : '?'} → {pattern.last_seen ? new Date(pattern.last_seen).toLocaleString('fr-FR') : '?'}
                        </div>
                      )}

                      {pattern.recommendation && (
                        <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#28a745' }}>
                          <strong>Recommandation :</strong> {pattern.recommendation}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* IPs suspectes */}
            {selectedReport.suspicious_ips?.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h5 style={{ margin: '0 0 10px', color: '#333' }}>IPs suspectes</h5>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>IP</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Raison</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedReport.suspicious_ips.map((ip, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '10px', borderBottom: '1px solid #f0f0f0', fontFamily: 'monospace' }}>{ip.ip}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>{ip.reason}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            background: ip.action === 'block' ? '#f8d7da' : '#fff3cd',
                            color: ip.action === 'block' ? '#721c24' : '#856404'
                          }}>
                            {ip.action}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Recommandations */}
            {selectedReport.recommendations?.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h5 style={{ margin: '0 0 10px', color: '#333' }}>Recommandations</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {selectedReport.recommendations.map((rec, idx) => (
                    <div key={idx} style={{
                      padding: '10px 12px',
                      background: '#e8f5e9',
                      borderRadius: '6px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: rec.priority === 'high' ? '#dc3545' : rec.priority === 'medium' ? '#ffc107' : '#28a745',
                        color: 'white'
                      }}>
                        {rec.priority}
                      </span>
                      <span>{rec.action}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Modules de sécurité avancée */}
            {selectedReport.advanced_analysis && (
              <div style={{ marginBottom: '20px' }}>
                <h5 style={{ margin: '0 0 10px', color: '#333' }}>
                  🛡️ Modules de sécurité avancée
                  <span style={{ fontWeight: 'normal', fontSize: '12px', color: '#666', marginLeft: '10px' }}>
                    ({selectedReport.advanced_analysis.total_anomalies || 0} anomalie(s) détectée(s))
                  </span>
                </h5>

                {/* Statut des modules */}
                {selectedReport.advanced_analysis.modules && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '15px' }}>
                    {Object.entries(selectedReport.advanced_analysis.modules).map(([module, active]) => (
                      <span key={module} style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: active ? '#d4edda' : '#f8d7da',
                        color: active ? '#155724' : '#721c24'
                      }}>
                        {module === 'temporal' && '⏱️ Temporel'}
                        {module === 'correlation' && '🔗 Corrélation'}
                        {module === 'ueba' && '👤 UEBA'}
                        {module === 'ipReputation' && '🎯 Scoring IP'}
                        {module === 'ddos' && '🛑 DDoS'}
                        {module === 'clustering' && '🔍 Clustering'}
                        {active ? ' ✓' : ' ✗'}
                      </span>
                    ))}
                  </div>
                )}

                {/* Répartition par sévérité */}
                {selectedReport.advanced_analysis.by_severity && Object.keys(selectedReport.advanced_analysis.by_severity).length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Par sévérité :</div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {Object.entries(selectedReport.advanced_analysis.by_severity).map(([severity, count]) => (
                        <span key={severity} style={{
                          padding: '4px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          background: RISK_LEVEL_CONFIG[severity]?.bgColor || '#f8f9fa',
                          color: RISK_LEVEL_CONFIG[severity]?.color || '#333'
                        }}>
                          {RISK_LEVEL_CONFIG[severity]?.label || severity}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Répartition par type */}
                {selectedReport.advanced_analysis.by_type && Object.keys(selectedReport.advanced_analysis.by_type).length > 0 && (
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>Par type d'anomalie :</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {Object.entries(selectedReport.advanced_analysis.by_type).map(([type, count]) => (
                        <span key={type} style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          background: '#e9ecef',
                          color: '#495057',
                          fontFamily: 'monospace'
                        }}>
                          {type}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Message si aucune anomalie */}
                {selectedReport.advanced_analysis.total_anomalies === 0 && (
                  <div style={{ padding: '15px', background: '#d4edda', borderRadius: '8px', color: '#155724' }}>
                    ✅ Aucune anomalie détectée par les 6 modules de sécurité avancée.
                  </div>
                )}
              </div>
            )}

            {/* Analyse détaillée */}
            {selectedReport.detailed_analysis && Object.keys(selectedReport.detailed_analysis).length > 0 && (
              <div>
                <h5 style={{ margin: '0 0 10px', color: '#333' }}>Analyse détaillée</h5>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {Object.entries(selectedReport.detailed_analysis).map(([key, value]) => (
                    <div key={key} style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                      <div style={{ fontWeight: '600', marginBottom: '5px', textTransform: 'capitalize' }}>
                        {key.replace(/_/g, ' ')}
                      </div>
                      <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityReportsTab;
